import { useQuery } from '@tanstack/react-query';

import type { MaintenanceIncident, StatusFeed } from './maintenance-incident';

/**
 * Fetches the public status feed (`/status/data.json`) and returns its incident
 * array. The feed is proxied SAME-ORIGIN by the dashboard's own Amplify app (an
 * Amplify rewrite → the StatusStack S3 object, mirroring how MarketingStack
 * serves it for the /status page; see WebStack `statusDataProxyTarget` + D30).
 * The feed still lives in a DIFFERENT failure domain than the api Lambda (the
 * StatusStack writer publishes it to S3) — exactly so a maintenance/outage
 * banner can render even when the api is the thing that's down.
 *
 * Same-origin is covered by the dashboard CSP `connect-src 'self'`, so no CSP
 * change is needed and the prior cross-origin CORS caveat is resolved.
 *
 * Fail-SAFE: any error (network failure, non-200, malformed JSON) resolves to
 * an empty list, so a flaky feed never throws into the dashboard chrome — the
 * banner simply shows nothing.
 */

const FEED_PATH = '/status/data.json';
const REFETCH_MS = 5 * 60 * 1000; // matches the status writer's 5-min cadence
const STALE_MS = 60 * 1000;

async function fetchIncidents(signal: AbortSignal): Promise<readonly MaintenanceIncident[]> {
  try {
    const res = await fetch(FEED_PATH, { signal });
    if (!res.ok) return [];
    const doc = (await res.json()) as StatusFeed;
    const incidents = doc.incidents;
    // Defensive against a malformed feed: only an actual array reaches the
    // selector (which calls `.filter`). `Array.isArray` widens the cast value
    // to `any[]`, so re-assert the element type to keep the return typed.
    return Array.isArray(incidents) ? (incidents as readonly MaintenanceIncident[]) : [];
  } catch {
    return [];
  }
}

export function useMaintenanceIncidents(): readonly MaintenanceIncident[] {
  const { data } = useQuery({
    queryKey: ['maintenance-incidents'],
    queryFn: ({ signal }) => fetchIncidents(signal),
    refetchInterval: REFETCH_MS,
    staleTime: STALE_MS,
    retry: false,
  });
  return data ?? [];
}
